/**
 * Lo que `tokens.css` pide en JS y no puede hacer solo (secciones 6, 7 y 9).
 * Tres cosas, y ninguna es opcional:
 *
 *  1. El tema (`data-theme`): oscuro por defecto.
 *  2. El congelado del cristal durante el scroll — `data-scrolling="1"`, retirado
 *     120 ms después del último evento. Es el truco de mayor retorno en fps de
 *     todo el sistema: `backdrop-filter` escala su coste de forma cuadrática con
 *     el radio, y durante un scroll se recompone en cada fotograma.
 *  3. El modo sólido automático en equipos flojos — `data-transparency="reduced"`.
 *     El conmutador manual de Ajustes escribe el mismo atributo.
 *
 * Nunca se anima la transición entre cristal y sólido: el salto se nota menos
 * que un fundido de 200 ms a mitad de scroll.
 */

export type Theme = 'dark' | 'light';
export type Transparency = 'standard' | 'reduced';

export const DEFAULT_THEME: Theme = 'dark';

/** Milisegundos de calma antes de devolver el blur. */
const SCROLL_SETTLE_MS = 120;

export function applyTheme(theme: Theme, root: HTMLElement = document.documentElement): void {
  root.dataset['theme'] = theme;
}

export function applyTransparency(
  value: Transparency,
  root: HTMLElement = document.documentElement,
): void {
  if (value === 'reduced') root.dataset['transparency'] = 'reduced';
  else delete root.dataset['transparency'];
}

type NavigatorWithMemory = Navigator & { deviceMemory?: number };

/**
 * Decide el modo sólido sin preguntar. Los dos umbrales son los del sistema de
 * diseño: 4 GB o 4 hilos. `deviceMemory` solo existe en Chromium, así que en
 * WebKit manda `hardwareConcurrency` — y en iOS, que es donde más importa,
 * `hardwareConcurrency` sí está.
 */
export function detectTransparency(nav: Navigator = navigator): Transparency {
  const memory = (nav as NavigatorWithMemory).deviceMemory;
  const cores = nav.hardwareConcurrency;
  if (typeof memory === 'number' && memory <= 4) return 'reduced';
  if (typeof cores === 'number' && cores > 0 && cores <= 4) return 'reduced';
  return 'standard';
}

/**
 * Marca `data-scrolling` mientras se hace scroll. Devuelve la función de limpieza.
 * Escucha en captura y en `window` para coger también los contenedores internos.
 */
export function installScrollFreeze(
  root: HTMLElement = document.documentElement,
  settleMs: number = SCROLL_SETTLE_MS,
): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined;

  const onScroll = (): void => {
    if (timer === undefined) root.dataset['scrolling'] = '1';
    else clearTimeout(timer);
    timer = setTimeout(() => {
      timer = undefined;
      delete root.dataset['scrolling'];
    }, settleMs);
  };

  window.addEventListener('scroll', onScroll, { capture: true, passive: true });

  return () => {
    window.removeEventListener('scroll', onScroll, { capture: true });
    if (timer !== undefined) clearTimeout(timer);
    delete root.dataset['scrolling'];
  };
}

export type ShellOptions = {
  theme?: Theme;
  /** `undefined` = detectar. Un valor explícito es el conmutador de Ajustes. */
  transparency?: Transparency;
  root?: HTMLElement;
};

/** Arranca las tres cosas de una vez. Devuelve la limpieza del scroll. */
export function installShell(options: ShellOptions = {}): () => void {
  const root = options.root ?? document.documentElement;
  applyTheme(options.theme ?? DEFAULT_THEME, root);
  applyTransparency(options.transparency ?? detectTransparency(), root);
  return installScrollFreeze(root);
}
